import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Bot,
    ClipboardCheck,
    Globe,
    MessageSquare,
    Phone,
    Repeat,
    Workflow,
} from "lucide-react";
import { services } from "@/lib/offers";
import {
    CompanyHeader,
    CompanyTabs,
    offerBtn,
    SmartLink,
} from "@/components/CompanyUi";
import { DiagnosticCta } from "@/components/DiagnosticCta";

const icons = {
    Globe,
    MessageSquare,
    Workflow,
    Repeat,
    Phone,
    Bot,
    ClipboardCheck,
};

export default function ServicesPage() {
    const [params, setParams] = useSearchParams();
    const slugs = services.map((s) => s.slug);
    const tab = slugs.includes(params.get("tab")) ? params.get("tab") : services[0].slug;
    const current = services.find((s) => s.slug === tab) || services[0];
    const Icon = icons[current.icon] || Globe;

    useEffect(() => {
        document.title = `${current.title} — Services — SalesCan`;
        return () => {
            document.title = "SalesCan";
        };
    }, [current.title]);

    return (
        <main data-testid="services-page" className="bg-white text-[#0a0a0b]">
            <CompanyHeader
                kicker="Services"
                title="Seven lines. Real work."
                subtitle="Websites, capture, CRM, workflows, missed-call recovery, custom agents, and the diagnostic. Not a 12-card brochure."
            />

            <section className="px-5 pb-20">
                <div className="mx-auto max-w-5xl">
                    <CompanyTabs
                        tabs={services.map((s) => ({ id: s.slug, label: s.title }))}
                        active={tab}
                        onChange={(id) => setParams({ tab: id }, { replace: true })}
                        testId="services-tabs"
                    />

                    <article className="mt-8 rounded-3xl bg-[#f6f7fb] p-6 ring-1 ring-black/5 sm:p-8">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[var(--brand)] ring-1 ring-black/5">
                                    <Icon className="h-6 w-6" />
                                </span>
                                <div>
                                    <h2 className="font-display text-2xl font-extrabold">{current.title}</h2>
                                    <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-[#5b5b66]">
                                        {current.short}
                                    </p>
                                </div>
                            </div>
                            <div className="rounded-2xl bg-white px-4 py-3 text-right ring-1 ring-black/5">
                                <p className="text-xs font-semibold uppercase tracking-wide text-[#8a8a95]">From</p>
                                <p className="font-display text-xl font-extrabold text-[var(--brand)]">{current.price}</p>
                            </div>
                        </div>

                        <div className="mt-8 grid gap-6 sm:grid-cols-2">
                            <Block title="What you get" items={current.features} />
                            <Block title="Deliverables" items={current.deliverables} />
                        </div>

                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            <p className="rounded-2xl bg-white p-4 text-sm leading-relaxed text-[#5b5b66] ring-1 ring-black/5">
                                <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand)]">
                                    Process
                                </span>
                                <span className="mt-2 block">{current.process}</span>
                            </p>
                            <p className="rounded-2xl bg-white p-4 text-sm leading-relaxed text-[#5b5b66] ring-1 ring-black/5">
                                <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand)]">
                                    Limits
                                </span>
                                <span className="mt-2 block">{current.limits}</span>
                            </p>
                        </div>

                        {current.faq.length > 0 ? (
                            <div className="mt-6 space-y-3">
                                {current.faq.map((item) => (
                                    <div key={item.q} className="rounded-2xl bg-white p-4 ring-1 ring-black/5">
                                        <p className="font-semibold">{item.q}</p>
                                        <p className="mt-1 text-sm leading-relaxed text-[#5b5b66]">{item.a}</p>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        <div className="mt-8">
                            {current.slug === "audit" || current.slug === "crm" || current.slug === "workflows" ? (
                                <DiagnosticCta testId={`service-cta-${current.slug}`} />
                            ) : (
                                <SmartLink href={current.href} className={offerBtn} testId={`service-cta-${current.slug}`}>
                                    {current.cta}
                                </SmartLink>
                            )}
                        </div>
                    </article>
                </div>
            </section>
        </main>
    );
}

function Block({ title, items }) {
    return (
        <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--brand)]">{title}</h3>
            <ul className="mt-3 space-y-2">
                {items.map((item) => (
                    <li key={item} className="text-sm leading-relaxed text-[#5b5b66]">
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
}
